import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  deleteMember,
  resetMemberPassword,
  sendInvitation as requestInvitation,
  setMemberStatus,
  updateMemberEmail,
  validateApiResponse,
} from '../../../services';
import { MEMBER_STATUS } from '../constants/copy';
import { RESET_PASSWORD_EXPIRY_HOURS } from '../constants/memberDetail';
import { mapApiMemberToUi } from '../utils/mapApiMemberToUi';
import { useChangeUserType } from './useChangeUserType';

/**
 * Member detail actions. Every handler resolves `true` on success and `false` on failure
 * (an error toast is shown and nothing changes). On success the server's member is mapped
 * and handed to `onChange` so the page updates without a refetch.
 *
 * @param {object} args
 * @param {object} args.member UI member (see `mapApiMemberToUi`)
 * @param {(member: object) => void} args.onChange
 */
export const useMemberActions = ({ member, onChange }) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const inFlightRef = useRef(new Set());
  const { id, displayName, email: oldEmail, status } = member;

  // The type change keeps its own toasts (CT-5568); only the member it returns is mapped here.
  const handleTypeChanged = useCallback((body) => onChange(mapApiMemberToUi(body)), [onChange]);
  const { changeType: requestTypeChange } = useChangeUserType({ onChanged: handleTypeChanged });

  const run = useCallback(
    async (request, successKey, { applies = true, params, extra } = {}) => {
      // The same action twice at once (a double click on a button with no confirmation, a toast
      // action pressed twice) runs once.
      // Sent / resent / send again are one call: they share a key.
      const guardKey = successKey.includes('invitationSent') || successKey.includes('invitationResent') ? 'invite' : successKey;
      if (inFlightRef.current.has(guardKey)) return false;
      inFlightRef.current.add(guardKey);
      try {
        const { body } = validateApiResponse(await request(), t('memberDetail.toast.failed'));
        if (applies) onChange(mapApiMemberToUi(body));
        toastRef.current.success({
          title: t(`${successKey}.title`, { name: displayName }),
          message: t(`${successKey}.message`, { name: displayName, hours: RESET_PASSWORD_EXPIRY_HOURS, ...params }),
          ...extra,
        });
        return true;
      } catch (err) {
        toastRef.current.error({
          title: t('memberDetail.toast.failed'),
          message: err?.message ?? t('memberDetail.toast.unknown'),
        });
        return false;
      } finally {
        inFlightRef.current.delete(guardKey);
      }
    },
    [t, onChange, displayName],
  );

  const suspend = useCallback(
    () => run(() => setMemberStatus(id, 'suspend'), 'memberDetail.toast.suspended'),
    [run, id],
  );
  const changeType = useCallback(
    (userType) => requestTypeChange({ id, displayName }, userType),
    [requestTypeChange, id, displayName],
  );
  const resetPassword = useCallback(
    () => run(() => resetMemberPassword(id), 'memberDetail.toast.passwordReset', { applies: false }),
    [run, id],
  );
  const sendInvitation = useCallback(
    () => run(() => requestInvitation(id), 'memberDetail.toast.invitationSent'),
    [run, id],
  );
  const resendInvitation = useCallback(
    () => run(() => requestInvitation(id), 'memberDetail.toast.invitationResent'),
    [run, id],
  );
  // Revoking asks nothing: "Send again" in the toast invites the person once more.
  const revokeInvitation = useCallback(
    () =>
      run(() => setMemberStatus(id, 'revoke'), 'memberDetail.toast.invitationRevoked', {
        extra: { action: t('memberDetail.toast.invitationRevoked.action'), onAction: sendInvitation },
      }),
    [run, id, t, sendInvitation],
  );
  const remove = useCallback(() => run(() => setMemberStatus(id, 'remove'), 'memberDetail.toast.removed'), [run, id]);
  const reactivate = useCallback(
    () => run(() => setMemberStatus(id, 'reactivate'), 'memberDetail.toast.reactivated'),
    [run, id],
  );
  const deleteUser = useCallback(
    () => run(() => deleteMember(id), 'memberDetail.toast.deleted', { applies: false }),
    [run, id],
  );
  const editEmail = useCallback(
    (email) =>
      run(
        () => updateMemberEmail(id, email),
        // Never invited: nothing went out, so only the address changed.
        status === MEMBER_STATUS.NOT_INVITED ? 'memberDetail.toast.emailSaved' : 'memberDetail.toast.emailUpdated',
        { params: { email, oldEmail } },
      ),
    [run, id, oldEmail, status],
  );

  return useMemo(
    () => ({
      suspend,
      changeType,
      resetPassword,
      sendInvitation,
      resendInvitation,
      revokeInvitation,
      editEmail,
      remove,
      reactivate,
      deleteUser,
    }),
    [suspend, changeType, resetPassword, sendInvitation, resendInvitation, revokeInvitation, editEmail, remove, reactivate, deleteUser],
  );
};
